import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { AppModal, AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import ModuleComsaSideBarReports from './ModuleComsaSideBarReports';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import Translator from '../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath } from '../../../routing/helpers/router';
import errorHandler from '@/layout/components/error/ErrorHandler';
import ModuleComsaTertiaryPackagingAdd from '../../../layout/screenSpecifics/Modules/ComsaReports/Report/TertiaryPackaging/ModuleComsaTertiaryPackagingAdd';
import ModuleComsaTertiaryPackagingUpdate from '../../../layout/screenSpecifics/Modules/ComsaReports/Report/TertiaryPackaging/ModuleComsaTertiaryPackagingUpdate';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import {
  getFile,
  handleDisabled,
} from '../../../layout/screenSpecifics/Modules/ComsaReports/tools/comsaReportsTools';

export default function ModuleComsaTertiaryPackagingScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const [reportList, setReportList] = useState([]);
  const [selectedDeleteReport, setSelectedDeleteReport] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [moduleData, setModuleData] = useState(null);
  const [reportType] = useState('tertiaryPackaging');

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_comsaReports_getModuleData'),
        { moduleBridgeHex, subModuleBridgeHex, plantHex },
      );
      return response.data;
    } catch (err) {}
  }, []);

  useEffect(() => {
    loadModuleData().then((res) => {
      if (isMounted.current) setModuleData(res);
    });
  }, []);
  // > LOAD MODULE DATA //

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  const loadReports = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_comsaReports_report'), {
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        })
        .then((r) => {
          if (isMounted.current) setReportList(r.data);
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false)),
    [],
  );

  useEffect(() => {
    setIsLoading(true);
    loadReports();
  }, []);

  const handleDeleteConfirm = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_comsaReports_removeReport'), {
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
          id: selectedDeleteReport,
        })
        .then((r) => {
          if (isMounted.current) {
            loadReports();
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setSelectedDeleteReport(undefined)),
    [selectedDeleteReport],
  );

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${moduleData.plantName} - ${ReactDOMServer.renderToString(
                    <Translator file="module-legalCompliance" name="evaluation" />,
                  )}`,
                  route: {
                    name: 'user_module_legalCompliance_evaluation',
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="indicators" />,
                  ),
                  route: {
                    name: 'user_module_comsaReports_home',
                    params: {
                      moduleBridgeHex,
                      subModuleBridgeHex,
                      plantHex,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaReports"
                      name="tertiary-packaging"
                    />,
                  ),
                },
              ]
            : [{ name: '...' }]
        }
      />
      <section className="content">
        <ModuleComsaSideBarReports
          activeRoute="user_module_comsaReports_tertiary_packaging"
          moduleData={moduleData}
        />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator file="module-comsaReports" name="tertiary-packaging" />
            </Title>
          </div>
        </div>

        <ModuleComsaTertiaryPackagingAdd
          OnRechargeReports={() => loadReports()}
          moduleData={moduleData}
        />

        <div className="row mt-3">
          <div className="col-12">
            <div className="card sr-header">
              <div className="card-body">
                <div className="mb-5">
                  <Translator
                    file="module-comsaReports"
                    name="tertiary-packaging-indicator-description"
                    isHtml
                  />
                </div>
                {isLoading ? (
                  <div>
                    <Translator file="module-saicaReports" name="loading" />
                  </div>
                ) : reportList?.length > 0 ? (
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th className="col-1">
                          <Translator file="module-comsaReports" name="year" />
                        </th>
                        <th className="col-1">
                          <Translator
                            file="module-comsaReports"
                            name="total-amount-tertiary-packaging-used-year"
                          />
                        </th>
                        <th className="col-1">
                          <Translator
                            file="module-comsaReports"
                            name="total-quantity-reusable-tertiary-packaging-used-year"
                          />
                        </th>
                        <th className="col-1">
                          <Translator
                            file="module-comsaReports"
                            name="average-number-rotations-single-reusable-container-year"
                          />
                        </th>
                        <th className="col-2">
                          <Translator file="module-comsaReports" name="evidence" />
                        </th>
                        <th className="col-5">
                          <Translator
                            file="module-comsaReports"
                            name="m-cr-comment"
                          />
                        </th>
                        <th className="col-1" />
                      </tr>
                    </thead>
                    <tbody>
                      {reportList?.map((r, i) => (
                        <tr key={r.id}>
                          <td>{r.year}</td>
                          <td>{r.amountTertiaryPackagingUsedYear}</td>
                          <td>{r.quantityReusableTertiaryPackagingUsedYear}</td>
                          <td>{r.averageRotationsSameReusableContainerYear}</td>
                          <td>
                            {r.files &&
                              Object.keys(r.files).map((salt, index) => (
                                <span
                                  key={index}
                                  className="mr-1 pointer"
                                  title={r.files[salt].name}
                                  onClick={() =>
                                    getFile(
                                      moduleBridgeHex,
                                      subModuleBridgeHex,
                                      plantHex,
                                      reportType,
                                      r.id,
                                      salt,
                                      dispatch,
                                    )
                                  }
                                >
                                  <AppRemixIcon icon="file-text" />{' '}
                                </span>
                              ))}
                          </td>
                          <td>{r.comment}</td>
                          <td className="text-center">
                            <ModuleComsaTertiaryPackagingUpdate
                              report={r}
                              OnRechargeReports={() => loadReports()}
                              moduleData={moduleData}
                            />

                            <button
                              type="button"
                              className="btn btn-danger btn-xs"
                              onClick={() => setSelectedDeleteReport(r.id)}
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-comsaReports"
                                  name="delete"
                                />,
                              )}
                              disabled={handleDisabled(moduleData, r?.year)}
                            >
                              <AppRemixIcon icon="delete-bin-6" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="alert alert-info">
                    <Translator file="module-saicaReports" name="no.results" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <AppModal
          show={selectedDeleteReport !== undefined}
          type="danger"
          isConfirmBtn
          isCancelBtn
          confirmBtnText={<Translator file="module-comsaReports" name="delete" />}
          title={<Translator file="module-comsaReports" name="delete.report" />}
          onClose={() => setSelectedDeleteReport(undefined)}
          onConfirm={() => handleDeleteConfirm()}
        >
          <Translator file="module-comsaReports" name="confirm.delete.report" />
        </AppModal>
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;
